import api from '../../utils/api';
import type {
  PortfolioViewData,
  PortfolioProfile,
  PortfolioProject,
  PortfolioProjectOptions,
  PortfolioProjectsMeta,
  Language,
} from './types';

const EMPTY_PROJECT_OPTIONS: PortfolioProjectOptions = { skills: [], topics: [], storyFormats: [] };

// ---------------------------------------------------------------------------
// Public portfolio view (subdomain page)
// ---------------------------------------------------------------------------

export const fetchPortfolioViewData = async (
  page: number = 1,
  filters?: {
    clipPublicationIds?: number[];
    topicIds?: number[];
    skillIds?: number[];
    storyFormats?: string[];
    publicOnly?: boolean;
  }
): Promise<PortfolioViewData> => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  if (filters?.publicOnly) params.set('public_only', 'true');
  filters?.clipPublicationIds?.forEach(id => params.append('clip_publication_ids[]', String(id)));
  filters?.topicIds?.forEach(id => params.append('topic_ids[]', String(id)));
  filters?.skillIds?.forEach(id => params.append('skill_ids[]', String(id)));
  filters?.storyFormats?.forEach(f => params.append('story_formats[]', f));

  params.set('format', 'json');
  const response = await api.get<PortfolioViewData>(`/?${params.toString()}`);
  return response.data;
};

// All four profile writes PUT to `.json`. The Rails route for PUT /portfolio/profile
// has no `defaults: { format: :json }` (only the GET does), so a bare path plus the
// axios `Accept: application/json, text/plain, */*` header negotiates to HTML — Rails
// falls back to its first renderable type on the `*/*` wildcard. That hits the
// `format.html { redirect_to portfolios_url(subdomain:) }` branch, returning a 302 to
// the user's public subdomain instead of `head :ok`. The write commits, but axios
// follows the cross-origin redirect, the promise rejects, and the page shows a false
// "your profile was not updated" error. Pinning `.json` forces the `format.json`
// branch (`head :ok` / 422 with field errors). Mirrors `fetchPortfolioProfile`'s
// explicit `.json`. (eng-maintenance#20945 follow-up.)
export const updateAvailability = async (available: boolean): Promise<void> => {
  await api.put('/portfolio/profile.json', {
    user: { available_freelance: available },
    from_portfolio: true,
  });
};

export const markPredictedLanguagesSeen = async (): Promise<void> => {
  await api.get('/mark_predicted_languages_seen');
};

// ---------------------------------------------------------------------------
// Profile editing
// ---------------------------------------------------------------------------

export const fetchPortfolioProfile = async (): Promise<PortfolioProfile> => {
  const response = await api.get<{ profile: PortfolioProfile }>('/portfolio/profile.json');
  return response.data.profile;
};

export const updateProfile = async (userData: Record<string, unknown>): Promise<void> => {
  await api.put('/portfolio/profile.json', userData);
};

export const uploadAvatar = async (file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('user[avatar]', file);
  formData.append('from_portfolio', 'true');
  await api.put('/portfolio/profile.json', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteAvatar = async (): Promise<void> => {
  await api.put('/portfolio/profile.json', {
    remove_photo: 'true',
    from_portfolio: true,
  });
};

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

// Projects-only fetch for the post-mutation asset refresh (no options/meta needed).
// `limit` threads through to the backend `index` paging so a refresh after editing
// a project past the first 100 fetches the same breadth that's on screen — without
// it the server returns only the first page and the edit modal's asset list never
// updates for project 101+ (Bugbot, eng-maintenance#21323).
export const fetchPortfolioProjects = async (
  params?: { limit?: number }
): Promise<PortfolioProject[]> => {
  const qs = params?.limit != null ? `?limit=${params.limit}` : '';
  const response = await api.get<{ projects: PortfolioProject[] }>(`/portfolio/projects.json${qs}`);
  return response.data.projects;
};

// Combined fetch used on page load: the same `/portfolio/projects.json` payload
// now also carries the Skills/Topics/Format option catalog (eng-maintenance#21110)
// under `options`, plus the pagination `meta` (eng-maintenance#21323) the page uses
// to drive its "Load more" control. One request feeds the project list, the
// edit-modal pickers, and the pagination state; `fetchPortfolioProjects` above
// stays projects-only for the post-mutation asset refresh that doesn't need them.
//
// `page`/`limit` thread through to the backend `index` paging
// (`.page(params[:page] || 1).per(params[:limit] || PER_PAGE)`). The legacy
// projects page had a "Load more" affordance (classic `pagination_button_view` +
// `projects_collection#fetchMore`) that the React migration dropped, leaving
// projects past the first 100 unreachable; passing `page`/`limit` restores access.
// Omitting both preserves the original first-100 request for existing callers/tests.
export const fetchPortfolioProjectsAndOptions = async (
  params?: { page?: number; limit?: number }
): Promise<{
  projects: PortfolioProject[];
  options: PortfolioProjectOptions;
  meta: PortfolioProjectsMeta;
}> => {
  const query = new URLSearchParams();
  if (params?.page != null) query.set('page', String(params.page));
  if (params?.limit != null) query.set('limit', String(params.limit));
  const qs = query.toString();
  const response = await api.get<{
    projects: PortfolioProject[];
    options?: Partial<PortfolioProjectOptions>;
    meta?: Partial<PortfolioProjectsMeta>;
  }>(`/portfolio/projects.json${qs ? `?${qs}` : ''}`);
  // Normalize PER-KEY, not all-or-nothing: a payload with `options` present but
  // missing one list (e.g. `{ skills: [...] }` with no `topics`/`storyFormats`)
  // must still yield arrays, or the edit modal crashes mapping `undefined`
  // (Bugbot, eng-maintenance#21110). `?? EMPTY` alone only guards a wholly-absent
  // `options`.
  const opts = response.data.options;
  const meta = response.data.meta;
  const projects = response.data.projects;
  return {
    projects,
    options: {
      skills: opts?.skills ?? EMPTY_PROJECT_OPTIONS.skills,
      topics: opts?.topics ?? EMPTY_PROJECT_OPTIONS.topics,
      storyFormats: opts?.storyFormats ?? EMPTY_PROJECT_OPTIONS.storyFormats,
    },
    // `totalCount` drives the Load-more button (legacy parity:
    // `hasNextPage = total_count > size()`). Fall back to the returned length so a
    // payload without `meta` (older mock / unpaginated response) simply shows no
    // further page rather than crashing on `undefined`.
    meta: {
      currentPage: meta?.currentPage ?? 1,
      totalPages: meta?.totalPages ?? 1,
      totalCount: meta?.totalCount ?? (projects?.length ?? 0),
    },
  };
};

export const createProject = async (data: {
  title?: string;
  description?: string;
  url?: string;
  public?: boolean;
  // Manual-entry fallback fields (eng-maintenance#20455). When URL extraction
  // fails, the user fills these in by hand. The backend already permits them in
  // `clip_params` (`:story_format`, `skill_ids: []`, `topic_ids: []`) and skips
  // Diffbot when url + title are both present — so this is a pure type widening,
  // no runtime change for existing callers.
  story_format?: string;
  skill_ids?: number[];
  topic_ids?: number[];
  // parity-audit-r2 (L6): Behance import. When `behance_id` is present the BE
  // routes to BehanceClipBuilder (projects_controller#build_clip_from_params),
  // exactly as legacy did — `:behance_id` is already in `clip_params`.
  behance_id?: number;
}): Promise<PortfolioProject> => {
  const response = await api.post<{ project: PortfolioProject }>('/portfolio/projects', { clip: data });
  return response.data.project;
};

// parity-audit-r2 (L6): restore the legacy "Import from Behance" search step.
// Legacy GET /portfolio/projects/search?service=behance&username=<u> →
// `projects_controller#search_behance` → `BehanceProjectsSearch` → a list of the
// user's Behance projects `[{ id, title }]`. The migrated SPA had dropped this
// search entirely and POSTed a raw URL (which silently fell through to Diffbot).
export interface BehanceProject {
  id: number;
  title: string;
}

export const searchBehanceProjects = async (username: string): Promise<BehanceProject[]> => {
  const response = await api.get<{ count: number; behance_projects: BehanceProject[] }>(
    `/portfolio/projects/search?service=behance&username=${encodeURIComponent(username)}`,
  );
  return response.data.behance_projects ?? [];
};

// eng-maintenance#21068 — restore the legacy "Import a PDF" create flow.
//
// The migration ported the dropdown label but not its implementation; the React
// page had no PDF picker and no multipart create call, so "Import a PDF" fell
// back to the URL-import modal (PR #799 restored the file picker).
//
// PR #799 alone is NOT sufficient: the backend create path resolves a publication
// for every non-URL/non-Behance clip. `Portfolios::ProjectsController#create` ->
// `build_clip_from_params` -> `clip_publication_id`, and with NO publication data
// that method falls to `url = ""` -> `ClipPublication.find_or_create_publication_from_url("", nil, false)`,
// which raises `ArgumentError("Cant find publication without a valid URL")`,
// rescued as `ClipPublicationCreationError` -> `head :internal_server_error`. So a
// PDF-only payload 500s on submit. The legacy `new.js.hamlc` PDF form always
// carried a REQUIRED Client section — either an existing `clip[clip_publication_id]`
// or a new `publication[name]` + `publication[homepage_url]` — so the backend never
// hit the empty-URL path. The backend is byte-identical between classic and master;
// the only gap is the FE, so we restore the Client data the legacy form submitted.
//
// `clipPublicationId` -> existing client (legacy select2 path).
// `newPublication`    -> "Add a new client" path (name + homepage URL).
// Exactly one is expected; the modal enforces that before calling this.
export const createProjectFromPdf = async (
  file: File,
  title?: string,
  publication?: { clipPublicationId?: number; newPublication?: { name: string; homepageUrl: string } }
): Promise<PortfolioProject> => {
  const formData = new FormData();
  formData.append('clip[external]', file);
  if (title) formData.append('clip[title]', title);
  if (publication?.clipPublicationId) {
    formData.append('clip[clip_publication_id]', String(publication.clipPublicationId));
  } else if (publication?.newPublication) {
    formData.append('publication[name]', publication.newPublication.name);
    formData.append('publication[homepage_url]', publication.newPublication.homepageUrl);
  }
  const response = await api.post<{ project: PortfolioProject }>('/portfolio/projects', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.project;
};

// Client/publication typeahead source for the Import-a-PDF (and any future clip)
// flow — the same endpoint the legacy select2 widget used
// (`backbone/projects/views/clip_publication_view.js.coffee` ->
// `/clip_publications/autocomplete.json`). `ClipPublicationsController#autocomplete`
// returns an array of clip publications (`{ id, name, ... }`) for the term.
export interface ClipPublicationSearchResult {
  id: number;
  name: string;
}

export const searchClipPublications = async (
  term: string
): Promise<ClipPublicationSearchResult[]> => {
  const response = await api.get<ClipPublicationSearchResult[]>('/clip_publications/autocomplete.json', {
    params: { term },
  });
  return (response.data || []).map(({ id, name }) => ({ id, name }));
};

export const updateProject = async (
  id: number,
  data: Record<string, unknown>
): Promise<void> => {
  await api.put(`/portfolio/projects/${id}`, { clip: data });
};

export const deleteProject = async (id: number): Promise<void> => {
  await api.delete(`/portfolio/projects/${id}`);
};

export const reorderProjects = async (projectIds: number[]): Promise<void> => {
  await api.post('/portfolio/projects/sort', { project: projectIds });
};

// ---------------------------------------------------------------------------
// Project images (eng-maintenance#21004 — restore legacy image upload)
//
// Two-step flow, mirroring the legacy Backbone project_view.js.coffee:
//   1. POST /media/upload_file (multipart) -> creates an Asset, returns its id.
//   2. POST /clips/:clipId/assets/:assetId/assetting -> attaches the asset to
//      the project (Clip). ClipPolicy#edit? authorizes the owner.
// Both endpoints already exist on the backend; no new routes were added.
// ---------------------------------------------------------------------------

interface UploadedAssetResponse {
  id: number;
  url: string;
  small?: string;
  thumb?: string;
}

export const uploadProjectImage = async (
  projectId: number,
  file: File
): Promise<void> => {
  // Step 1 — upload the file, minting an Asset.
  const formData = new FormData();
  formData.append('file', file);
  const { data: asset } = await api.post<UploadedAssetResponse>('/media/upload_file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  // Step 2 — attach the asset to the project (Clip).
  await api.post(`/clips/${projectId}/assets/${asset.id}/assetting`);
};

export const deleteProjectImage = async (
  projectId: number,
  assetId: number
): Promise<void> => {
  await api.delete(`/clips/${projectId}/assets/${assetId}`);
};

export const bulkEditProjects = async (
  action: string,
  clipIds: number[]
): Promise<void> => {
  await api.post(`/portfolio/projects/bulk-edit/${action}`, { project_ids: clipIds });
};

// ---------------------------------------------------------------------------
// Skills / Topics / Languages (registration & edit)
// ---------------------------------------------------------------------------

export const fetchAllLanguages = async (): Promise<Language[]> => {
  const response = await api.get<{ languages: Language[] }>('/portfolio/register/languages.json');
  return response.data.languages;
};
